import { db, realtimeDb } from './firebase';
import { 
  doc, 
  updateDoc, 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  where, 
  getDoc 
} from 'firebase/firestore';
import { 
  ref, 
  set, 
  get, 
  remove 
} from 'firebase/database';

// Kelime listesini getir
export const getWords = async (level = 1) => {
  try {
    console.log('Kelimeler getiriliyor, seviye:', level);
    
    const wordsRef = collection(db, 'words');
    const q = query(
      wordsRef,
      where('levelName', '==', `Seviye ${level}`)
    );
    
    const snapshot = await getDocs(q);
    console.log('Firestore sorgu sonucu:', snapshot.size);

    if (snapshot.empty) {
      console.log('Bu seviyede kelime bulunamadı');
      return [];
    }

    const words = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Kelimeleri karıştır
    const shuffledWords = words.sort(() => Math.random() - 0.5);
    console.log('Yüklenen kelimeler:', shuffledWords);
    return shuffledWords;
  } catch (error) {
    console.error('Kelime yükleme hatası:', error);
    throw error;
  }
};

// Puan hesaplama fonksiyonu
const calculateScore = (timeLeft, combo) => {
  const baseScore = 10; // Temel puan
  const timeBonus = Math.floor(timeLeft * 2); // Kalan süreye göre bonus (her saniye için 2 puan)
  const comboBonus = Math.floor(combo * 5); // Combo bonusu (her combo için 5 puan)
  
  return baseScore + timeBonus + comboBonus;
};

// Seviye hesaplama yardımcı fonksiyonu
const calculateLevel = (score, levels) => {
  // Seviyeleri puanlara göre sırala
  const sortedLevels = levels.sort((a, b) => a.requiredScore - b.requiredScore);
  
  // En düşük seviyeden başlayarak kontrol et
  let currentLevel = 1;
  for (const level of sortedLevels) {
    if (score >= level.requiredScore) {
      currentLevel = parseInt(level.name.split(' ')[1]);
    } else {
      break;
    }
  }
  return currentLevel;
};

// Kullanıcı skorunu güncelle
export const updateUserScore = async (userId, timeLeft, combo = 0) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('Kullanıcı bulunamadı');
    }

    const userData = userDoc.data();
    const currentScore = userData.score || 0;
    
    // Yeni puanı hesapla
    const earnedPoints = calculateScore(timeLeft, combo);
    const newScore = currentScore + earnedPoints;
    
    // Seviye bilgilerini al
    const levelsRef = collection(db, 'levels');
    const levelsSnapshot = await getDocs(levelsRef);
    const levels = levelsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const currentLevel = calculateLevel(currentScore, levels);
    const newLevel = calculateLevel(newScore, levels);
    const levelUpBonus = newLevel > currentLevel ? 50 : 0;

    const totalEarnedPoints = earnedPoints + levelUpBonus;
    const finalScore = currentScore + totalEarnedPoints;

    // Kullanıcı verilerini güncelle
    await updateDoc(userRef, {
      score: finalScore,
      level: newLevel,
      combo: combo,
      maxCombo: Math.max(combo, userData.maxCombo || 0),
      correctAnswers: (userData.correctAnswers || 0) + 1,
      updatedAt: new Date()
    });

    return {
      earnedPoints: totalEarnedPoints,
      newScore: finalScore,
      newLevel,
      levelUp: newLevel > currentLevel,
      breakdown: {
        baseScore: 10,
        timeBonus: Math.floor(timeLeft * 2),
        comboBonus: Math.floor(combo * 5),
        levelUpBonus
      }
    };
  } catch (error) {
    console.error('Skor güncelleme hatası:', error);
    throw error;
  }
};

// Liderlik tablosunu getir
export const getLeaderboard = async () => {
  try {
    // Önce seviye bilgilerini al
    const levelsRef = collection(db, 'levels');
    const levelsSnapshot = await getDocs(levelsRef);
    const levels = levelsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Kullanıcıları getir
    const usersRef = collection(db, "users");
    const q = query(usersRef, orderBy("score", "desc"), limit(10));
    const querySnapshot = await getDocs(q);

    // Her kullanıcı için seviyeyi hesapla
    return querySnapshot.docs.map(doc => {
      const userData = doc.data();
      const userLevel = calculateLevel(userData.score || 0, levels);
      return {
        id: doc.id,
        ...userData,
        level: userLevel
      };
    });
  } catch (error) {
    console.error('Liderlik tablosu getirme hatası:', error);
    throw error;
  }
};

// Duo mode için oyun oluştur
export const createDuoGame = async (gameCode, hostId) => {
  const gameRef = ref(realtimeDb, `games/${gameCode}`);
  await set(gameRef, {
    hostId,
    status: 'waiting',
    createdAt: new Date().toISOString(),
    players: {
      [hostId]: {
        score: 0,
        ready: false
      }
    }
  });
  return gameCode;
};

// Duo mode oyununa katıl
export const joinDuoGame = async (gameCode, playerId) => {
  const gameRef = ref(realtimeDb, `games/${gameCode}`);
  const gameSnapshot = await get(gameRef);
  
  if (!gameSnapshot.exists()) {
    throw new Error('Game not found');
  }

  const gameData = gameSnapshot.val();
  if (gameData.status !== 'waiting') {
    throw new Error('Game already started');
  }

  await set(ref(realtimeDb, `games/${gameCode}/players/${playerId}`), {
    score: 0,
    ready: false
  });
};

// Duo mode oyununu sil
export const deleteDuoGame = async (gameCode) => {
  const gameRef = ref(realtimeDb, `games/${gameCode}`);
  await remove(gameRef);
};

// Seviye bilgilerini getir
export const getLevelInfo = async () => {
  try {
    const levelsRef = collection(db, 'levels');
    const snapshot = await getDocs(levelsRef);
    
    const levels = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).sort((a, b) => a.order - b.order);

    return levels;
  } catch (error) {
    console.error('Seviye bilgileri getirme hatası:', error);
    throw error;
  }
};
